import React, { forwardRef } from "react";
import DatePicker from "react-datepicker";
import styled from "styled-components";
import { Controller } from "react-hook-form";
import ValidationErrorText from "./ValidationErrorText";
import { useTranslation } from "react-i18next";

// Custom Input for DatePicker
const CustomInput = forwardRef(
  ({ value, onClick, disabled, placeholderText, icon: Icon, dir }, ref) => (
    <div className={`custom-input-wrapper ${dir}`} onClick={onClick}>
      <input
        ref={ref}
        value={value}
        readOnly
        disabled={disabled}
        placeholder={placeholderText}
        className={`form-control ${dir}`}
      />
      {Icon && (
        <span className="icon-wrapper">
          <Icon />
        </span>
      )}
    </div>
  )
);
CustomInput.displayName = "CustomInput";

const DatePickerRightIcon = ({
  control,
  name,
  rules,
  placeholder,
  icon: Icon,
  minDate,
  disabled,
  maxDate,
  yearDropdown,
  monthDropdown,
  onDateChange = null,
  ...rest
}) => {
  const { t, i18n } = useTranslation();
  const dir = i18n.dir(); // 'ltr' or 'rtl'

  return (
    <DatepickerWrapper dir={dir} {...rest}>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <DatePicker
              selected={value}
              disabled={disabled}
              onChange={(e) => {
                onChange(e);
                if (onDateChange) onDateChange(name, e);
              }}
              dateFormat="yyyy/MM/dd"
              showMonthDropdown={monthDropdown}
              showYearDropdown={yearDropdown}
              dropdownMode="select"
              customInput={
                <CustomInput
                  disabled={disabled}
                  placeholderText={placeholder}
                  icon={Icon}
                  dir={dir}
                />
              }
              minDate={minDate}
              maxDate={maxDate}
            />
            {error?.type === "required" && (
              <ValidationErrorText text={t("fieldRequired")} />
            )}
          </>
        )}
      />
    </DatepickerWrapper>
  );
};

// Styled component
const DatepickerWrapper = styled.div`
  width: 100%;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .custom-input-wrapper {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
  }

  .form-control {
    background-color: ${({ bg }) => (bg ? bg : "var(--white)")};
    border: 1px solid var(--borderLight);
    height: 48px;
    width: 100%;
    box-shadow: none;
    outline: none;
    border-radius: 10px;
    color: var(--text);
    font-size: 14px;
    padding: 0 42px 0 12px;

    &::placeholder {
      color: var(--text2);
    }
  }

  .form-control.rtl {
    padding: 0 12px 0 42px;
  }

  .icon-wrapper {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text2);
    font-size: 18px;
    pointer-events: none;
  }

  .custom-input-wrapper.ltr .icon-wrapper {
    right: 12px;
  }

  .custom-input-wrapper.rtl .icon-wrapper {
    left: 12px;
  }

  .form-control:focus {
    box-shadow: none;
    outline: none;
  }
`;

export default DatePickerRightIcon;
